import type { ModalProps } from 'antd';
import { Button, Flex, Form, Input, Modal } from 'antd';
import { useEffect, useState } from 'react';
import {
  useSendApplicantOtpMutation,
  useVerifyApplicantsOtpMutation,
} from '../app/features/auth/auth.api';
import { toast } from '../lib/toast';

type Props = {
  data?: any;
  type?: any;
  open?: boolean;
  handleClose: () => void;
} & ModalProps;

const LoginModal = ({ open, handleClose }: Props) => {
  const [form] = Form.useForm();
  const status = Form.useWatch('status', form);
  const [timeLeft, setTimeLeft] = useState(120);

  useEffect(() => {
    let timer: any;
    if (status === 'otp' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [status, timeLeft]);

  const [sendApplicantOtp, { isLoading: sendingOtp }] =
    useSendApplicantOtpMutation();
  const [verifyApplicantsOtp, { isLoading: verifyingOtp }] =
    useVerifyApplicantsOtpMutation();

  const handleSubmit = async () => {
    const values = await form.validateFields();

    // Send OTP from email screen
    if (status === 'email') {
      try {
        await sendApplicantOtp({ email: values.email }).unwrap();
        toast('success', 'OTP sent to your email');
        form.setFieldsValue({ status: 'otp', email: values.email });
        setTimeLeft(120);
      } catch (err: any) {
        toast('error', err?.data?.message || 'Failed to send OTP');
      }
      return;
    }

    // If we're on OTP screen and user entered OTP -> verify
    if (status === 'otp' && values?.otp) {
      try {
        await verifyApplicantsOtp({
          email: values.email,
          code: values.otp,
        }).unwrap();
        toast('success', 'Logged in successfully');
        form.resetFields();
        handleClose();
      } catch (err: any) {
        toast('error', err?.data?.message || 'OTP verification failed');
      }
      return;
    }

    // Otherwise (resend when timer expired)
    try {
      const email = form.getFieldValue('email');
      await sendApplicantOtp({ email }).unwrap();
      toast('success', 'OTP resent');
      setTimeLeft(120);
    } catch (err: any) {
      toast('error', err?.data?.message || 'Failed to resend OTP');
    }
  };

  return (
    <Modal
      open={open}
      width={400}
      title={null}
      footer={null}
      closeIcon={false}
      onCancel={() => {
        form.resetFields();
        handleClose();
      }}
    >
      <Form
        form={form}
        size="large"
        layout="vertical"
        className="custom-form space-y-3 mt-5"
      >
        <Form.Item name="status" noStyle hidden initialValue="email" />
        <Form.Item name="email" noStyle hidden />
        <Flex justify="center" className="text-xl font-semibold">
          {status === 'email' ? 'Log In' : 'Verify OTP'}
        </Flex>
        {status === 'email' ? (
          <Form.Item
            name="email"
            rules={[
              {
                required: true,
                message: 'Please enter email',
              },
            ]}
          >
            <Input type="email" placeholder="Enter a email address" />
          </Form.Item>
        ) : (
          <Form.Item
            name="otp"
            rules={[
              {
                required: true,
                message: 'Please enter otp',
              },
            ]}
          >
            <Input.OTP
              disabled={timeLeft === 0 || verifyingOtp}
              onChange={(value: string) => {
                form.setFieldsValue({ otp: value });
                // auto-submit when full
                if (value?.length === 6) {
                  void handleSubmit();
                }
              }}
              length={6}
            />
          </Form.Item>
        )}

        {status === 'otp' && (
          <Flex justify="space-between" align="center">
            <span className="text-theme-gray-500 text-sm font-normal">
              {Math.floor(timeLeft / 60)}:{timeLeft % 60 < 10 ? '0' : ''}
              {timeLeft % 60}
            </span>

            <div className="flex items-center gap-2">
              <Button
                onClick={handleSubmit}
                loading={sendingOtp}
                disabled={timeLeft > 0 || sendingOtp}
                className="btn-primary h-10 min-w-24 disabled:!text-white disabled:hover:!text-white disabled:!opacity-70 disabled:hover:!opacity-70"
              >
                Resend OTP
              </Button>
            </div>
          </Flex>
        )}
        {status === 'email' && (
          <Flex justify="end" align="center">
            <Button
              className="btn-primary h-10 min-w-24"
              onClick={handleSubmit}
              loading={sendingOtp}
            >
              Send OTP
            </Button>
          </Flex>
        )}
      </Form>
    </Modal>
  );
};

export default LoginModal;
